/**
 * Gate: determine whether the price-selector ladder can safely be surfaced
 * when the checkout was initiated from an event/offer context.
 *
 * Spike findings (KRA-213, 2026-06-05):
 *
 * Rolling offer (benefitType === 'rollingOffer'):
 *   Purchase middleware in player-backend (`eventTrigger.middleware.ts` ~L677-682)
 *   and core rolling-offer-events.service.ts (~L117-118) both require an exact
 *   packageId match within `benefit.benefitPackages[].packageId`. Upgrading to
 *   an unlisted store SKU will fail the middleware check with PackageNotAvailableError.
 *   → SAFE to show selector only when ALL ladder ids are in benefitPackages.
 *
 * AFTER_PURCHASE trigger (extra.triggerPackages):
 *   If `eventTrigger.extra` carries `triggerPackages`, the core event.service.ts
 *   (~L1149-1167) returns early with no benefit if purchased packageId is not listed.
 *   The `triggerPackages` field is not typed in the frontend EventTriggerUniques shape
 *   (it is a runtime addition). Until the frontend type is updated and manual QA
 *   confirms which ladder ids qualify, this case conservatively returns false.
 *
 * All other benefit types (offer, 2-3-offer, quest, road, personalGift, …):
 *   Benefit eligibility coupling to a specific packageId is not yet verified
 *   for every type. Default to false (no selector) to avoid missing event rewards.
 *   Widen per-type in follow-up tickets once QA confirms safe upgrade paths.
 */

import type { EventTriggerPurchaseData } from '../../../stores/app/types';
import type { RollingOfferPopupType } from '../../../stores/event-trigger/types';

export function canShowPriceSelectorForActiveEvent(
  activeEvent: EventTriggerPurchaseData['activeEvent'] | undefined,
  ladderPackageIds: number[],
): boolean {
  if (!activeEvent) {
    return true;
  }

  const { benefit } = activeEvent;

  if (benefit.benefitType === 'rollingOffer') {
    const rollingBenefit = benefit as unknown as RollingOfferPopupType;
    const allowedIds = new Set(rollingBenefit.benefitPackages.map((bp) => bp.packageId));
    return ladderPackageIds.every((id) => allowedIds.has(id));
  }

  return false;
}
